import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock3, ImageIcon, User2 } from "lucide-react";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { fetchBlogPost } from "../lib/api";
import BlogTemplateRenderer from "../components/BlogTemplateRenderer";
import RichText from "../components/RichText";
import { getTemplateMeta } from "../lib/blogTemplates";
import { stripRichText } from "../lib/richText";

function formatDate(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useDocumentMeta({
    title: stripRichText(post?.seo_title) || "FindClarity Blog",
    description:
      stripRichText(post?.seo_description) ||
      "FindClarity articles on mental health, counseling support, and emotional wellbeing.",
  });

  useEffect(() => {
    let active = true;

    async function loadPost() {
      try {
        const data = await fetchBlogPost(slug);
        if (!active) {
          return;
        }
        setPost(data);
        setStatus("ready");
      } catch (error) {
        if (!active) {
          return;
        }
        setStatus("error");
      }
    }

    loadPost();

    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="blog-post-page">
        <div className="blog-post-shell">
          <div className="blog-empty-state">
            <ImageIcon size={28} />
            <h2>Loading article</h2>
            <p>Preparing the article layout and imagery for you.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error" || !post) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-shell">
          <div className="blog-empty-state">
            <ImageIcon size={28} />
            <h2>Article not found</h2>
            <p>This blog either does not exist yet or could not be loaded.</p>
            <Link to="/blogs" className="blogs-primary-action">
              Back to Blogs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const templateMeta = getTemplateMeta(post.template);

  return (
    <div className="blog-post-page">
      <section className={`blog-article-hero blog-article-hero-${post.template || "listicle"}`}>
        <img
          src={post.hero_image}
          alt={post.hero_image_alt}
          className="blog-article-hero-image"
        />
        <div className="blog-article-hero-overlay" />
        <div className="blog-article-hero-content">
          <Link to="/blogs" className="blog-back-link">
            <ArrowLeft size={18} />
            Back to Blogs
          </Link>
          <div className="blog-hero-chip-row">
            <span className="blog-chip blog-chip-light">{post.category}</span>
            <span className="blog-chip blog-chip-light blog-chip-template">
              {templateMeta.label}
            </span>
          </div>
          <RichText as="h1" value={post.title} multiline={false} />
          <RichText className="blog-hero-excerpt" value={post.excerpt} />
          <div className="blog-article-meta">
            <span>
              <CalendarDays size={16} />
              {formatDate(post.published_at)}
            </span>
            <span>
              <Clock3 size={16} />
              {post.read_time}
            </span>
            <span>
              <User2 size={16} />
              {post.author}
            </span>
          </div>
        </div>
      </section>

      <section className="blog-post-shell">
        <BlogTemplateRenderer post={post} />

        {post.supporting_gallery.length > 0 && (
          <section className="blog-gallery-panel">
            <div className="blog-gallery-header">
              <h2>Supporting visuals</h2>
              <p>
                Use these images to reinforce the feeling and message of the
                article.
              </p>
            </div>
            <div className="blog-gallery-grid">
              {post.supporting_gallery.map((imageUrl, index) => (
                <img
                  src={imageUrl}
                  alt={`${stripRichText(post.title)} supporting visual ${index + 1}`}
                  className="blog-gallery-image"
                  key={imageUrl}
                />
              ))}
            </div>
          </section>
        )}
      </section>
    </div>
  );
};

export default BlogPost;
